/*
 * File: resumeApi.ts
 *
 * resumeApi: RTK Query APIs for the resume process.
 *
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { PageContent } from "../types";

import { AxiosError } from "axios";

import {
  buildStrippedJsonFromValues,
  initAndGetSeonPayload,
  transformAxiosToFetchBaseQueryErrorWithLocationRedirect,
} from "../utils";
import {
  apiOnboardingKeyName,
  apiOnboardingKeyValue,
  endpoints,
  getResumeFetcher,
} from "./ApiConfig";

export type ResumeStartRequestData = {
  email?: string | null;
  phone?: number | string | null;
  prefix?: number | string | null;
  countryCode?: number | string | null;
  processId?: string | null;
  existsMultipleProcessToResume?: string | null;
};

export const resumeApi = createApi({
  reducerPath: "resumeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: endpoints.resume,
    prepareHeaders: headers => {
      if (apiOnboardingKeyName && apiOnboardingKeyValue) {
        headers.set(apiOnboardingKeyName, apiOnboardingKeyValue);
      }
      return headers;
    },
  }),
  tagTypes: ["Resume"],
  endpoints: builder => ({
    resumeStart: builder.mutation<
      PageContent,
      {
        data?: ResumeStartRequestData;
        productType?: string;
        analyticsId?: string;
      }
    >({
      async queryFn({ data, productType, analyticsId }) {
        try {
          await initAndGetSeonPayload();
          let bodyData: Record<string, unknown> | undefined = undefined;
          if (productType) {
            bodyData = { productType };
          }
          if (analyticsId) {
            bodyData = { ...bodyData, analyticsId };
          }
          if (data?.email || data?.processId) {
            const validData = Object.keys(data).reduce<Record<string, unknown>>(
              (acc, curr) => {
                const key = curr as keyof typeof data;
                if (data[key]) {
                  acc[key] = data[key];
                }
                return acc;
              },
              {},
            );
            bodyData = { ...bodyData, ...validData };
          }
          const res = await getResumeFetcher(undefined, undefined, true).post(
            "/start",
            bodyData,
            {
              withCredentials: true,
            },
          );
          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
          );
          return { error };
        }
      },
    }),
    resumeNext: builder.mutation<
      PageContent,
      {
        stepId?: number;
        data: PageContent;
        values: Record<string, unknown>;
      }
    >({
      async queryFn({ values, data }) {
        const parsedData = buildStrippedJsonFromValues({
          pageContent: data,
          values,
          filterByVisibility: true,
        });
        try {
          const seonPayload = await initAndGetSeonPayload();
          const res = await getResumeFetcher(undefined, undefined, true).patch(
            "/next",
            parsedData,
            {
              withCredentials: true,
              headers: { seonPayload },
            },
          );
          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
          );
          return { error };
        }
      },
    }),
    resumeBack: builder.mutation<
      PageContent,
      { stepId?: number; processId: string }
    >({
      async queryFn({ processId }) {
        const headers:
          | Headers
          | string[][]
          | Record<string, string | undefined> = {};
        try {
          const res = await getResumeFetcher().get(`back/${processId}`, {
            withCredentials: true,
            headers,
          });
          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
          );
          return { error };
        }
      },
    }),
    otpSend: builder.mutation<{ otpId: string }, string | void>({
      async queryFn(processId) {
        try {
          const res = await getResumeFetcher().post("/otp/send", processId);
          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
          );
          return { error };
        }
      },
    }),
  }),
});

export const {
  useResumeStartMutation,
  useResumeBackMutation,
  useResumeNextMutation,
  useOtpSendMutation,
} = resumeApi;

export const allResumeFulfilledMatchers = [
  resumeApi.endpoints.resumeBack.matchFulfilled,
  resumeApi.endpoints.resumeNext.matchFulfilled,
  resumeApi.endpoints.resumeStart.matchFulfilled,
];

export const allResumeRejectedMatchers = [
  resumeApi.endpoints.resumeBack.matchRejected,
  resumeApi.endpoints.resumeNext.matchRejected,
  resumeApi.endpoints.resumeStart.matchRejected,
];

export const allResumePendingMatchers = [
  resumeApi.endpoints.resumeBack.matchPending,
  resumeApi.endpoints.resumeNext.matchPending,
  resumeApi.endpoints.resumeStart.matchPending,
];
